import React, { useCallback, useEffect, useRef, useState } from "react";
import { Printer, ChevronRight, ArrowLeft, Users, Mail } from "lucide-react";
import { api } from "./api";
import { Tableau, Cellule, Badge, Eyebrow, Section, Recherche, Puces, Case, BarreSelection, BoutonSombre, usePagePaysage } from "./ui";
import EssaisSemaine from "./EssaisSemaine";

const JOURS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const LOGO = "/img/9ab2fa8bea.webp";
// Les mêmes mots que le Planning pour l'état des places (calculé par le serveur).
const LIBELLES_ETAT = {
  OUVERT: "Ouvert", DERNIERES_PLACES: "Dernières places", COMPLET: "Complet", FERME: "Fermé",
};

export default function Liste({ cible, onFil }) {
  const [types, setTypes] = useState([]);
  const [type, setType] = useState("cours");
  const [q, setQ] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [jour, setJour] = useState("");
  const [data, setData] = useState({ items: [], disciplines: [] });
  const [chargement, setChargement] = useState(true);
  const [fiche, setFiche] = useState(null);
  const [feuilles, setFeuilles] = useState(null);
  const [choisis, setChoisis] = useState([]);
  const [inactifs, setInactifs] = useState(false);
  // « Essais » : les essais de la semaine, tous les cours confondus (Jen, 03.11.2026). Le
  // nombre sur la pastille est celui de la semaine en cours ; un nouveau clic recharge.
  const [nbEssais, setNbEssais] = useState("");
  const [tourEssais, setTourEssais] = useState(0);

  useEffect(() => { api("/liste/types").then(setTypes).catch(() => {}); }, []);
  useEffect(() => { api("/essais/semaine").then((d) => setNbEssais(d.nb)).catch(() => {}); }, []);
  const pastilles = types.flatMap((t) => (t.id === "cours" ? [t, { id: "essais", label: "Essais", nb: nbEssais }] : [t]));

  /* « Quand je clique sur Stage, les cours semestriels restent ; je dois recliquer
     plusieurs fois » (école, 09.10.2026). La liste des cours met plusieurs secondes à
     arriver du serveur, celle des stages une fraction : la réponse des COURS, demandée
     avant le clic, arrivait APRÈS celle des stages et l'écrasait — le tableau « Stages »
     montrait les cours. Chaque demande porte maintenant un numéro : seule la dernière a
     le droit d'écrire, et le tableau n'affiche que les données de l'onglet choisi. */
  const derniere = useRef(0);
  const charger = useCallback(() => {
    // « Essais » charge sa propre période (EssaisSemaine) : une réponse des cours encore en
    // route n'a plus le droit d'écrire.
    if (type === "essais") { derniere.current += 1; setChargement(false); return; }
    const p = new URLSearchParams({ q, discipline, jour, inactifs: inactifs ? "true" : "false" });
    const numero = ++derniere.current;
    setChargement(true);
    api(`/liste/${type}?${p}`)
      .then((d) => { if (numero === derniere.current) setData({ disciplines: [], ...d, type }); })
      .catch(() => {})
      .finally(() => { if (numero === derniere.current) setChargement(false); });
  }, [type, q, discipline, jour, inactifs]);

  useEffect(() => { const t = setTimeout(charger, q ? 220 : 0); return () => clearTimeout(t); }, [charger, q]);

  const ouvrir = (id) => api(`/liste/${type}/fiche?id=${encodeURIComponent(id)}`)
    .then((f) => setFiche({ ...f, groupe_id: id })).catch(() => {});

  useEffect(() => {
    if (!cible) return;
    // Une cible peut viser un autre onglet que les cours : « spectacles::Spectacle de Noël ».
    const [t, id] = String(cible).includes("::")
      ? [String(cible).split("::")[0], String(cible).split("::").slice(1).join("::")]
      : ["cours", String(cible)];
    setType(t);
    api(`/liste/${t}/fiche?id=${encodeURIComponent(id)}`)
      .then((f) => setFiche({ ...f, groupe_id: id })).catch(() => {});
  }, [cible]);

  // Une feuille ouverte : son nom monte dans le fil d'Ariane de la console (« Liste › Stage
  // d'automne… ») et « Retour à la liste » s'affiche tout en haut, au-dessus du sous-menu
  // « Listes / Cours privés » — là où l'œil va d'abord (demande de l'école, 09.10.2026).
  const ouverte = feuilles
    ? `${feuilles.length} feuille${feuilles.length > 1 ? "s" : ""} de présence`
    : (fiche ? fiche.entete?.titre || fiche.cours?.nom || "Feuille" : "");
  useEffect(() => {
    if (!onFil) return;
    onFil(ouverte
      ? { titre: ouverte, retour: () => { setFeuilles(null); setFiche(null); charger(); } }
      : null);
  }, [ouverte, onFil, charger]);
  useEffect(() => () => { if (onFil) onFil(null); }, [onFil]);

  const imprimerLot = async () => {
    const d = await api(`/liste/cours/fiches?ids=${encodeURIComponent(choisis.join(","))}`);
    setFeuilles(d.fiches);
  };

  if (feuilles) return <Feuilles fiches={feuilles} onImprime={charger} />;
  // Une semaine plus tôt ou plus tard : la feuille de cette séance-là, avec SES absences.
  const changerSeance = (f, jours) => {
    const d = new Date(`${f.seance_le}T12:00:00`);
    d.setDate(d.getDate() + jours);
    const le = d.toISOString().slice(0, 10);
    // Le groupe suit la fiche : « Écrire à ce groupe » vise toujours le même cours.
    api(`/liste/cours/fiche?id=${encodeURIComponent(f.cours.id)}&le=${le}`)
      .then((nf) => setFiche({ ...nf, groupe_id: f.groupe_id || f.cours.id })).catch(() => {});
  };
  // Les billets de juin (Liste › Spectacles) n'ont pas d'envoi groupé : on écrit depuis
  // Spectacles › Billets & commandes.
  if (fiche) return <Feuilles fiches={[fiche]} onImprime={charger}
    ecrireA={fiche.type === "billets" ? null : () => window.dispatchEvent(new CustomEvent("console-aller", {
      detail: { onglet: "envois", id: `${fiche.type || type}::${fiche.groupe_id || ""}` } }))}
    onSeance={fiche.type === "cours" && fiche.seance_le ? (j) => changerSeance(fiche, j) : null} />;

  // Les lignes d'un autre onglet (réponse arrivée trop tard) ne s'affichent jamais.
  const lignes = data.type === type ? data.items : [];
  const avecEleves = lignes.filter((c) => (c.nb_inscrits || 0) + (c.nb_essais || 0) > 0);
  const tous = avecEleves.length > 0 && avecEleves.every((c) => choisis.includes(c.id));
  // Les cours qui ont accueilli quelqu'un depuis leur dernière impression : c'est la
  // seule pile de feuilles qui vaut la peine d'être ressortie cette semaine.
  const bouges = avecEleves.filter((c) => (c.nb_nouveaux || 0) > 0);

  return (
    <div className="space-y-5" data-testid="console-liste">
      <div className={type === "essais" ? "sans-impression" : ""}>
        <Eyebrow>Cours, essais, stages, auditions, spectacles et workshops</Eyebrow>
        <h1 className="font-display text-3xl tracking-tight mt-1">Liste</h1>
      </div>

      {/* Premier niveau de filtre, comme sur le planning du site */}
      <div className={`flex flex-wrap gap-2${type === "essais" ? " sans-impression" : ""}`} data-testid="liste-filtres-type">
        {pastilles.map((t) => (
          <button
            key={t.id}
            data-testid={`liste-type-${t.id}`}
            onClick={() => {
              // Un clic sur l'onglet déjà choisi le recharge (avant : rien ne se passait).
              if (t.id === "essais" && type === "essais") { setTourEssais((n) => n + 1); return; }
              if (t.id === type) { charger(); return; }
              // La table de l'ancienne famille est vidée tout de suite : on ne reste
              // pas plusieurs secondes sur des cours en croyant voir des stages.
              setType(t.id); setDiscipline(""); setJour(""); setQ("");
              setChoisis([]); setData({ items: [], disciplines: [] }); setChargement(true);
            }}
            className="px-4 py-2.5 rounded-full text-sm transition-transform hover:-translate-y-0.5"
            style={{
              background: type === t.id ? "var(--primary)" : "#fff",
              color: type === t.id ? "#04202A" : "var(--muted)",
              border: `1px solid ${type === t.id ? "var(--primary)" : "var(--border)"}`,
              fontWeight: type === t.id ? 600 : 400,
            }}
          >
            {t.label}{t.nb === "" ? null : <span style={{ opacity: 0.65 }}> ({t.nb})</span>}
          </button>
        ))}
      </div>

      {type === "essais" && <EssaisSemaine tour={tourEssais} onNbSemaine={setNbEssais} />}

      {type !== "essais" && (
      <div className="flex flex-wrap items-center gap-3">
        {type === "cours" && (
          <>
            <select className="input-field" style={{ width: 200 }} value={discipline}
              onChange={(e) => { setDiscipline(e.target.value); setChoisis([]); }} data-testid="liste-filtre-discipline">
              <option value="">Toutes les disciplines</option>
              {data.disciplines.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
            <select className="input-field" style={{ width: 160 }} value={jour}
              onChange={(e) => { setJour(e.target.value); setChoisis([]); }} data-testid="liste-filtre-jour">
              <option value="">Tous les jours</option>
              {JOURS.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
          </>
        )}
        <div className="ml-auto">
          <Recherche valeur={q} onChange={setQ} largeur={300} testid="liste-recherche"
            placeholder="Nom, professeur, salle…" />
        </div>
      </div>
      )}

      {type === "cours" && (data.nb_inactifs || 0) > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-1" data-testid="liste-bloc-inactifs">
          <button className="btn-ghost" data-testid="liste-voir-inactifs"
            onClick={() => { setInactifs(!inactifs); setChoisis([]); }}>
            {inactifs
              ? "Masquer les cours inactifs"
              : `Voir les ${data.nb_inactifs} cours inactifs`}
          </button>
          <span className="text-xs text-[var(--muted)]">
            « Inactif » veut dire que le cours est retiré du programme et n'apparaît plus sur
            le site — ce n'est pas une question de places. Se règle dans Planning.
          </span>
        </div>
      )}

      {type === "essais" ? null : type === "cours" ? (
        <>
          {avecEleves.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 px-1">
              <Case cochee={tous} onChange={(v) => setChoisis(v ? avecEleves.map((c) => c.id) : [])}
                testid="liste-tout-selectionner" titre="Tout sélectionner" />
              <span className="text-xs text-[var(--muted)]">
                Tout sélectionner ({avecEleves.length} cours avec des élèves{jour ? ` le ${jour.toLowerCase()}` : ""})
              </span>
              <button className="btn-ghost" data-testid="liste-selectionner-nouveaux"
                disabled={bouges.length === 0}
                onClick={() => setChoisis(bouges.map((c) => c.id))}
                style={bouges.length === 0 ? { opacity: 0.45, cursor: "not-allowed" } : { borderColor: "#F59E0B", color: "#B45309" }}>
                <Printer size={13} /> Sélectionner les cours avec des nouveaux ({bouges.length})
              </button>
              <span className="text-xs text-[var(--muted)]">
                {bouges.length === 0
                  ? "Aucun cours n'a bougé depuis sa dernière impression — rien à réimprimer."
                  : "Nouvelles inscriptions et essais arrivés depuis la dernière impression de la feuille."}
              </span>
            </div>
          )}

          {choisis.length > 0 && (
            <BarreSelection nb={choisis.length} testid="liste-barre">
              <BoutonSombre onClick={imprimerLot} testid="liste-imprimer-lot">
                <Printer size={14} /> Préparer les {choisis.length} feuilles
              </BoutonSombre>
            </BarreSelection>
          )}

          <Tableau
            testid="tableau-liste-cours"
            onClic={(c) => ouvrir(c.id)}
            entetes={["", "Cours", "Quand", "Professeur", "Salle", { label: "Inscrits", droite: true }, { label: "Attente", droite: true }, { label: "Essais", droite: true }, "Nouveaux", "Places", ""]}
            lignes={lignes}
            vide={chargement ? "Chargement…" : "Aucun cours avec ces filtres."}
            rendu={(c) => (
              <>
                <Cellule>
                  {(c.nb_inscrits || 0) + (c.nb_essais || 0) > 0 && (
                    <Case cochee={choisis.includes(c.id)} testid="liste-case"
                      onChange={(v) => setChoisis(v ? [...choisis, c.id] : choisis.filter((x) => x !== c.id))} />
                  )}
                </Cellule>
                <Cellule fort>{c.nom}<div className="text-xs font-normal text-[var(--muted)]">{c.discipline} · {c.age_affiche}</div></Cellule>
                <Cellule>{c.jour} {c.debut}–{c.fin}</Cellule>
                <Cellule>{c.professeur}</Cellule>
                <Cellule muet>{c.salle}</Cellule>
                <Cellule droite fort>
                  <span style={{ color: c.nb_inscrits < 4 ? "#B91C1C" : "inherit" }}>{c.nb_inscrits}</span>
                </Cellule>
                <Cellule droite muet>{c.nb_attente || "—"}</Cellule>
                <Cellule droite muet>{c.nb_essais || "—"}</Cellule>
                <Cellule>
                  {(c.nb_nouveaux || 0) === 0
                    ? <span className="text-xs text-[var(--muted)]">à jour</span>
                    : (
                      <span data-testid="liste-badge-nouveaux">
                        <Badge ton="ambre">
                          {c.jamais_imprime ? "jamais imprimé"
                            : (c.nb_nouveaux - (c.nouveaux_fusion || 0) > 0
                              ? `${c.nb_nouveaux - (c.nouveaux_fusion || 0)} nouveau${c.nb_nouveaux - (c.nouveaux_fusion || 0) > 1 ? "x" : ""}`
                              : `${c.nouveaux_fusion} par fusion`)}
                        </Badge>
                        {!c.jamais_imprime && (
                          <div className="text-[10px] text-[var(--muted)] mt-0.5">
                            {[c.nouveaux_inscriptions ? `${c.nouveaux_inscriptions} semestriel(s)` : "",
                              c.nouveaux_essais ? `${c.nouveaux_essais} essai(s)` : "",
                              c.nouveaux_fusion ? `${c.nouveaux_fusion} par fusion de cours` : ""].filter(Boolean).join(" · ")}
                          </div>
                        )}
                      </span>
                    )}
                </Cellule>
                <Cellule>
                  {(c.statut || "").toLowerCase().startsWith("inactif")
                    ? <Badge ton="ambre">Fermé</Badge>
                    : (
                      <Badge ton={c.etat_places === "COMPLET" ? "rouge" : c.etat_places === "DERNIERES_PLACES" ? "ambre" : "vert"}>
                        {LIBELLES_ETAT[c.etat_places] || c.etat_places || "—"}
                      </Badge>
                    )}
                </Cellule>
                <Cellule muet><ChevronRight size={16} /></Cellule>
              </>
            )}
          />
        </>
      ) : (
        <Tableau
          testid={`tableau-liste-${type}`}
          onClic={(i) => ouvrir(i.id)}
          entetes={["Nom", "Quand", { label: "Inscrits", droite: true }, ...(type === "spectacles" ? [{ label: "Personnes", droite: true }] : []), ""]}
          lignes={lignes}
          vide={chargement ? "Chargement…" : "Rien pour l'instant."}
          rendu={(i) => (
            <>
              <Cellule fort>{i.nom}</Cellule>
              <Cellule muet>{i.quand || "—"}</Cellule>
              <Cellule droite fort>{i.nb_inscrits}</Cellule>
              {type === "spectacles" && <Cellule droite>{i.nb_personnes}</Cellule>}
              <Cellule muet><ChevronRight size={16} /></Cellule>
            </>
          )}
        />
      )}
    </div>
  );
}

/* ── Les feuilles à imprimer ─────────────────────────────────────────────────
   Une feuille par cours, en-tête à l'identité de l'école, sauts de page entre
   les cours : on sélectionne un jour, on imprime la journée entière. */
function Feuilles({ fiches, onImprime, onSeance, ecrireA }) {
  const cours = fiches.filter((f) => f.type === "cours");
  const autres = fiches.filter((f) => f.type !== "cours");
  // Les feuilles de présence des cours sortent en portrait ; les listes de stage,
  // d'audition, de spectacle et de workshop, plus larges, en paysage.
  usePagePaysage(autres.length > 0);

  // Une feuille sortie sur papier est notée comme imprimée : ce repère sert ensuite à
  // savoir quels cours ont accueilli quelqu'un de nouveau depuis.
  const imprimer = async () => {
    window.print();
    if (cours.length === 0) return;
    try {
      await api("/liste/cours/imprimees", {
        method: "POST", body: JSON.stringify({ ids: cours.map((f) => f.cours.id) }),
      });
      if (onImprime) onImprime();
    } catch { /* l'impression a eu lieu : on ne bloque pas l'école pour un repère */ }
  };

  return (
    <div className="space-y-6 fiche-impression" data-testid="console-fiche">
      <div className="flex flex-wrap items-center gap-3 sans-impression">
        <span className="text-sm text-[var(--muted)]">
          {fiches.length === 1 ? "1 feuille prête" : `${fiches.length} feuilles prêtes — une page par cours`}
        </span>
        {onSeance && (
          <span className="flex items-center gap-1" data-testid="fiche-seances">
            <button onClick={() => onSeance(-7)} className="btn-ghost text-xs" data-testid="fiche-seance-precedente">
              <ArrowLeft size={13} /> Séance précédente
            </button>
            <button onClick={() => onSeance(7)} className="btn-ghost text-xs" data-testid="fiche-seance-suivante">
              Séance suivante <ArrowLeft size={13} style={{ transform: "rotate(180deg)" }} />
            </button>
          </span>
        )}
        {/* Les bonnes personnes sont déjà réunies sous les yeux de l'école : autant
            pouvoir leur écrire d'ici, au lieu de recopier les adresses à la main. */}
        {ecrireA && (
          <button onClick={ecrireA} className="btn-ghost" data-testid="fiche-ecrire-groupe">
            <Mail size={15} /> Écrire à ce groupe
          </button>
        )}
        <button onClick={imprimer} className="btn-primary ml-auto" data-testid="fiche-imprimer">
          <Printer size={15} /> Imprimer {fiches.length > 1 ? `les ${fiches.length} feuilles` : "la feuille"}
        </button>
      </div>

      {cours.map((f, k) => <FeuilleCours key={f.cours.id} fiche={f} derniere={k === cours.length - 1 && !autres.length} />)}
      {autres.map((f) => <FeuilleAutre key={f.entete.titre} fiche={f} />)}
    </div>
  );
}

const Entete = ({ titre, sous_titre, infos, eyebrow }) => (
  <div className="rounded-2xl overflow-hidden border feuille-entete" style={{ borderColor: "var(--border)" }}>
    <div className="px-6 py-5 flex flex-wrap items-center gap-5" style={{ background: "#0F1B2D" }}>
      <img src={LOGO} alt="1000 ARTS" style={{ height: 46, width: "auto", filter: "brightness(0) invert(1)" }} />
      <div className="min-w-0">
        <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: "#7DE8ED" }}>{eyebrow}</div>
        <div className="font-display text-2xl tracking-tight" style={{ color: "#fff" }}>{titre}</div>
      </div>
      <div className="ml-auto text-right text-xs" style={{ color: "rgba(255,255,255,0.72)" }}>
        {sous_titre}
      </div>
    </div>
    {infos && (
      <div className="px-6 py-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" style={{ background: "#F7FAFC" }}>
        {infos.map(([label, valeur]) => (
          <div key={label}>
            <div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">{label}</div>
            <div className="text-sm font-semibold mt-0.5">{valeur === 0 ? 0 : (valeur || "—")}</div>
          </div>
        ))}
      </div>
    )}
  </div>
);

const TitreBloc = ({ children, nb, essai }) => (
  <div className="flex items-center gap-2 mt-6 mb-2 pb-1.5 border-b" style={{ borderColor: essai ? "#F59E0B" : "var(--primary)" }}>
    <h2 className="font-display text-base uppercase tracking-[0.06em]" style={{ color: essai ? "#B45309" : "var(--primary)" }}>
      {children}
    </h2>
    <span className="text-xs font-semibold text-[var(--muted)]">{nb}</span>
  </div>
);

const CaseVide = () => (
  <span className="inline-block" style={{ width: 20, height: 20, border: "1.4px solid #94A3B8", borderRadius: 4 }} />
);

/* La présence d'un jour (stage) ou d'une séance (workshop) : l'appel fait au téléphone
   (mode école), corrigeable d'un clic ici (vide → présent·e → absent·e → vide). « — » :
   l'enfant ne vient pas ce jour-là (jours cochés par l'école). */
const SUIVANT = { "": "present", present: "absent", absent: "" };
const CasePresence = ({ etat, attendu = true, onChange, testid }) => {
  if (!attendu) {
    return <span className="text-[var(--muted)]" title="Pas inscrit·e ce jour-là" data-testid={`${testid}-hors`}>—</span>;
  }
  const contenu = etat === "present"
    ? <span className="inline-grid place-items-center font-bold" style={{ width: 20, height: 20, borderRadius: 4, background: "#16A34A", color: "#fff" }}>✓</span>
    : etat === "absent"
      ? <span className="inline-grid place-items-center font-bold" style={{ width: 20, height: 20, borderRadius: 4, background: "#DC2626", color: "#fff" }}>✗</span>
      : <CaseVide />;
  if (!onChange) return <span data-testid={testid} data-etat={etat || "vide"}>{contenu}</span>;
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onChange(SUIVANT[etat || ""]); }} data-testid={testid}
      data-etat={etat || "vide"} title={etat === "present" ? "Présent·e — cliquer pour absent·e" : etat === "absent" ? "Absent·e — cliquer pour effacer" : "Cliquer pour présent·e"}>
      {contenu}
    </button>
  );
};
const aujourdhuiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const Neuf = () => (
  <span className="ml-2 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full"
    style={{ background: "#CFFAFE", color: "#0E7490" }} data-testid="feuille-badge-nouveau">NOUVEAU</span>
);

/* Arrivé·e par une fusion de cours (Planning) : pas nouveau·elle à l'école, son groupe a
   été réuni avec celui-ci — d'où une mention discrète au lieu de NOUVEAU (école, 09.10.2026). */
const ParFusion = ({ f }) => (
  <span className="ml-2 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full" title={f.cours ? `Avant : ${f.cours}${f.avant ? `, ${f.avant}` : ""}${f.professeur ? `, ${f.professeur}` : ""}` : ""}
    style={{ background: "#EEF2F6", color: "#475569" }} data-testid="feuille-badge-fusion">
    FUSION{f.avant ? ` · avant ${f.avant}` : ""}
  </span>
);

/* Carte 10 cours : le professeur tamponne la carte papier à chaque séance. */
const Carte10 = () => (
  <span className="ml-2 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full"
    style={{ background: "#FEF3C7", color: "#B45309" }} data-testid="feuille-badge-carte10">CARTE 10 COURS</span>
);

/* Son anniversaire tombe le jour de cette séance (et ce jour-là seulement). */
const Anniversaire = () => (
  <span className="ml-2 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full"
    style={{ background: "#FCE7F3", color: "#BE185D" }} data-testid="feuille-badge-anniversaire">🎂 ANNIVERSAIRE</span>
);

/* Annoncée par la famille depuis l'application, pour la séance de cette feuille SEULEMENT. */
const Absent = ({ motif }) => (
  <span className="ml-2 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full" title={motif || ""}
    style={{ background: "#FEF3C7", color: "#92400E" }} data-testid="feuille-badge-absent">
    ABSENT{motif ? ` — ${motif}` : ""}
  </span>
);

const TableauFeuille = ({ entetes, largeurs, lignes, vide, testid }) => (
  <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid={testid}>
    <table className="w-full" style={{ fontSize: 12.5, borderCollapse: "collapse" }}>
      <colgroup>{(largeurs || []).map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
      <thead>
        <tr style={{ background: "#EEF2F6" }}>
          {entetes.map((h, i) => (
            <th key={typeof h === "string" ? h : `${h.label}-${i}`}
              className={`px-3 py-2 text-left text-[9.5px] font-semibold uppercase tracking-[0.1em] whitespace-nowrap ${(h.cls || "")}`}
              style={{ color: "#334155" }}>{typeof h === "string" ? h : h.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {lignes.length === 0 && <tr><td className="px-3 py-4 text-[var(--muted)] text-xs" colSpan={entetes.length}>{vide}</td></tr>}
        {lignes}
      </tbody>
    </table>
  </div>
);

// La saison en cours, lue une seule fois auprès du serveur (calendrier des Réglages) :
// l'en-tête des feuilles imprimées ne porte plus une saison écrite en dur.
let saisonPromesse = null;
const useSaisonCourante = () => {
  const [saison, setSaison] = useState("");
  useEffect(() => {
    if (!saisonPromesse) saisonPromesse = api("/saisons").then((r) => r.courante || "").catch(() => "");
    let vivant = true;
    saisonPromesse.then((s) => { if (vivant) setSaison(s); });
    return () => { vivant = false; };
  }, []);
  return saison;
};
const libelleSaison = (fiche, courante) => {
  const s = fiche?.saison || courante;
  return s ? `saison ${s}` : "saison en cours";
};

const FeuilleCours = ({ fiche, derniere }) => {
  const c = fiche.cours;
  const courante = useSaisonCourante();
  const saison = fiche.semestre ? fiche.semestre.replace("·", "—") : libelleSaison(fiche, courante);
  return (
    <section className="feuille" style={{ breakAfter: derniere ? "auto" : "page" }} data-testid="feuille-cours">
      <Entete
        eyebrow="Feuille de présence"
        titre={c.nom}
        sous_titre={<>1000 ARTS · {saison}<br />{c.discipline} · {c.age_affiche}</>}
        infos={[
          ["Séance", `${c.jour} ${String(fiche.seance_le || "").split("-").reverse().join(".")} · ${c.debut}–${c.fin}`],
          ["Salle", c.salle],
          ["Professeur", c.professeur],
          ["Inscrits", `${fiche.inscrits.length} / ${c.places_max || "—"}`],
          ["Essais à venir", fiche.essais.length],
          ["Liste d'attente", fiche.attente.length],
          ["Dernière impression", fiche.imprime_le
            ? `${String(fiche.imprime_le).slice(0, 10).split("-").reverse().join(".")}${fiche.nb_nouveaux - (fiche.nb_fusion || 0) > 0 ? ` · ${fiche.nb_nouveaux - (fiche.nb_fusion || 0)} nouveau(x)` : ""}${fiche.nb_fusion ? ` · ${fiche.nb_fusion} par fusion` : ""}`
            : "jamais imprimée"],
        ]}
      />

      <TitreBloc nb={`${fiche.inscrits.length} élève(s)`}>Élèves inscrits</TitreBloc>
      <TableauFeuille
        testid="fiche-inscrits"
        entetes={["N°", "Élève", "Âge", "Téléphone", "Droit image", "Infos", "Présent·e"]}
        largeurs={["4%", "28%", "7%", "16%", "10%", "24%", "11%"]}
        vide="Aucun élève inscrit pour l'instant."
        lignes={fiche.inscrits.map((p, k) => (
          <tr key={p.inscription_id || k} style={{ background: k % 2 ? "#F9FBFC" : "#fff", borderTop: "1px solid #E9EEF3" }}>
            <td className="px-3 py-2 text-[var(--muted)]">{k + 1}</td>
            <td className="px-3 py-2 font-semibold">
              {p.eleve || "—"}
              {p.nouveau && <Neuf />}
              {p.anniversaire && <Anniversaire />}
              {p.fusion && <ParFusion f={p.fusion} />}
              {p.carte10 && <Carte10 />}
              {p.absent && <Absent motif={p.absence_motif} />}
              {/* Désinscrite mais le semestre est payé : elle vient encore, le professeur
                  doit la voir sur sa feuille avec son dernier jour de cours. */}
              {p.desinscrit && (
                <span className="ml-2 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full"
                  style={{ background: "#FEE2E2", color: "#B91C1C" }} data-testid="fiche-inscrit-desinscrit">
                  désinscrit·e{p.dernier_cours_le ? ` — dernier cours le ${String(p.dernier_cours_le).split("-").reverse().join(".")}` : ""}
                </span>
              )}
              <div className="text-[11px] font-normal text-[var(--muted)]">{p.responsable}</div>
            </td>
            <td className="px-3 py-2">{p.age !== "" ? `${p.age} ans` : "—"}</td>
            <td className="px-3 py-2 whitespace-nowrap">{p.telephone || "—"}</td>
            <td className="px-3 py-2">
              {(p.droit_image || "").toLowerCase().startsWith("o")
                ? <span style={{ color: "#15803D", fontWeight: 600 }}>oui</span>
                : (p.droit_image ? <span style={{ color: "#B91C1C", fontWeight: 600 }}>non</span> : "—")}
            </td>
            <td className="px-3 py-2 text-[11.5px]">{p.infos || ""}</td>
            <td className="px-3 py-2"><CaseVide /></td>
          </tr>
        ))}
      />

      {/* Automatique : tous les essais réservés qui n'ont pas encore eu lieu. */}
      <TitreBloc essai nb={`${fiche.essais.length} personne(s)`}>Cours d'essai à venir</TitreBloc>
      <TableauFeuille
        testid="fiche-essais"
        entetes={["N°", "Élève", "Âge", "Responsable", "Téléphone", "Date de l'essai", "Présent·e"]}
        largeurs={["4%", "24%", "7%", "20%", "16%", "18%", "11%"]}
        vide="Personne n'a réservé d'essai à venir dans ce cours."
        lignes={fiche.essais.map((p, k) => (
          <tr key={`${p.eleve_id}-${k}`} style={{ background: p.aujourdhui ? "#FFFBEB" : (k % 2 ? "#F9FBFC" : "#fff"), borderTop: "1px solid #E9EEF3" }}>
            <td className="px-3 py-2 text-[var(--muted)]">{k + 1}</td>
            <td className="px-3 py-2 font-semibold">
              {p.eleve || "—"}
              {p.nouveau && <Neuf />}
              {p.anniversaire && <Anniversaire />}
              {p.absent && <Absent motif={p.absence_motif} />}
              {p.aujourdhui && <span className="ml-2 text-[9.5px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: "#FDE68A", color: "#92400E" }}>aujourd'hui</span>}
            </td>
            <td className="px-3 py-2">{p.age !== "" ? `${p.age} ans` : "—"}</td>
            <td className="px-3 py-2">{p.responsable || "—"}</td>
            <td className="px-3 py-2 whitespace-nowrap">{p.telephone || "—"}</td>
            <td className="px-3 py-2 whitespace-nowrap">{p.date ? p.date.split("-").reverse().join(".") : "—"}</td>
            <td className="px-3 py-2"><CaseVide /></td>
          </tr>
        ))}
      />

      {fiche.attente.length > 0 && (
        <>
          <TitreBloc nb={`${fiche.attente.length} personne(s)`}>Liste d'attente</TitreBloc>
          <TableauFeuille
            testid="fiche-attente"
            entetes={["N°", "Élève", "Responsable", "Téléphone", "Statut"]}
            largeurs={["4%", "30%", "26%", "20%", "20%"]}
            vide=""
            lignes={fiche.attente.map((p, k) => (
              <tr key={p.inscription_id || k} style={{ background: k % 2 ? "#F9FBFC" : "#fff", borderTop: "1px solid #E9EEF3" }}>
                <td className="px-3 py-2 text-[var(--muted)]">{k + 1}</td>
                <td className="px-3 py-2 font-semibold">{p.eleve}</td>
                <td className="px-3 py-2">{p.responsable || "—"}</td>
                <td className="px-3 py-2">{p.telephone || "—"}</td>
                <td className="px-3 py-2 text-[var(--muted)]">{p.statut || "Liste d'attente"}</td>
              </tr>
            ))}
          />
        </>
      )}

      {fiche.desinscrits.length > 0 && (
        <div className="sans-impression">
          <TitreBloc nb={`${fiche.desinscrits.length}`}>Partis et annulés</TitreBloc>
          <TableauFeuille
            testid="fiche-desinscrits"
            entetes={["Élève", "Annoncé le", "Dernier cours", "Statut"]}
            largeurs={["34%", "22%", "22%", "22%"]}
            vide=""
            lignes={fiche.desinscrits.map((p, k) => (
              <tr key={p.inscription_id || k} style={{ borderTop: "1px solid #E9EEF3" }}>
                <td className="px-3 py-2 font-semibold">{p.eleve}</td>
                <td className="px-3 py-2 text-[var(--muted)]">{String(p.le || "").slice(0, 10).split("-").reverse().join(".") || "—"}</td>
                <td className="px-3 py-2 text-[var(--muted)]">{String(p.dernier_cours_le || "").split("-").reverse().join(".") || "—"}</td>
                <td className="px-3 py-2 text-[var(--muted)]">{p.statut}</td>
              </tr>
            ))}
          />
        </div>
      )}

      <div className="mt-4 text-[10px] text-[var(--muted)] flex flex-wrap gap-4 pied-feuille">
        <span>1000 ARTS · Chemin des Dailles 10E, 1053 Cugy VD · info1000arts@gmail.com</span>
        <span className="ml-auto">Feuille éditée depuis la console — {new Date().toLocaleDateString("fr-CH")}</span>
      </div>
    </section>
  );
};

/* Stages, auditions et spectacles : mêmes en-tête et mêmes tables, colonnes
   fournies par le serveur. Le groupe, le statut et le résultat se règlent ici, dans
   des menus déroulants qui ne partent pas à l'impression : sur le papier ne restent
   que les personnes inscrites, avec la place pour noter le résultat à la main. */
export const estInscrit = (l) => {
  const s = (l.statut || "Inscrit").toLowerCase();
  return !s.startsWith("annul") && !s.startsWith("désinscrit") && !s.startsWith("desinscrit");
};

export const SelectLigne = ({ valeur, options, onChange, testid }) => (
  <select
    className="sans-impression"
    data-testid={testid}
    value={valeur || ""}
    onClick={(e) => e.stopPropagation()}
    onChange={(e) => onChange(e.target.value)}
    style={{
      fontSize: 12, padding: "4px 8px", borderRadius: 8, background: "#fff",
      border: "1px solid var(--border)", maxWidth: 150,
    }}
  >
    {/* Une valeur venue du classeur qui n'est pas dans la liste (« Confirmé » pour un
        spectacle) reste affichée telle quelle : sinon le menu montrait « Inscrit » alors
        que la base disait autre chose. */}
    {[...(options.includes(valeur || "") ? [] : [valeur || ""]), ...options]
      .map((o) => <option key={o} value={o}>{o || "—"}</option>)}
  </select>
);

function FeuilleAutre({ fiche }) {
  const saisonCourante = useSaisonCourante();
  const [lignes, setLignes] = useState(fiche.lignes);
  const [filtres, setFiltres] = useState({});
  const [q, setQ] = useState("");
  const [eclate, setEclate] = useState(false);
  const [erreur, setErreur] = useState("");
  const colonnesFiltre = fiche.filtres || [];
  const modifiables = fiche.modifiables || [];
  const principal = colonnesFiltre[0];
  // Stage : une colonne par jour ; workshop (une date) : la colonne « Présent·e » relit l'appel.
  const joursStage = fiche.jours_stage?.jours || [];
  const [appelWs, setAppelWs] = useState(fiche.appel?.etats || {});
  const auj = aujourdhuiIso();

  // Chercher une personne dans la liste : le nom, l'e-mail, le téléphone — tout le contenu
  // de sa ligne. Une liste de spectacle fait 30 lignes, on ne les relit pas à l'œil.
  const cherche = q.trim().toLowerCase();
  const visibles = lignes.filter((l) =>
    colonnesFiltre.every(({ colonne }) => !filtres[colonne] || (l[colonne] || "—") === filtres[colonne])
    && (!cherche || Object.values(l).some((v) => String(v ?? "").toLowerCase().includes(cherche))));
  const actifs = colonnesFiltre.filter(({ colonne }) => filtres[colonne]);

  // Une colonne vide pour tout le monde disparaît : c'est ainsi que la catégorie,
  // réservée à la compétition, ne s'imprime pas sur une feuille de compagnie.
  const colonnes = fiche.colonnes
    .map((colonne, i) => ({ colonne, label: fiche.entetes[i] }))
    .filter(({ colonne }) => colonne === "prenom" || colonne === "nom"
      || visibles.some((l) => String(l[colonne] ?? "").trim() !== ""));

  const options = (colonne) => {
    const compte = {};
    lignes.forEach((l) => { const v = l[colonne] || "—"; compte[v] = (compte[v] || 0) + 1; });
    return [{ valeur: "", label: "Tous", nb: lignes.length },
      ...Object.keys(compte).sort().map((v) => ({ valeur: v, label: v, nb: compte[v] }))];
  };

  const modifier = async (ligne, colonne, valeur) => {
    const avant = ligne[colonne];
    setLignes((ls) => ls.map((x) => (x.id === ligne.id ? { ...x, [colonne]: valeur } : x)));
    setErreur("");
    try {
      await api(`/participations/${fiche.type}/${encodeURIComponent(ligne.id)}`, {
        method: "PATCH", body: JSON.stringify({ champs: { [colonne]: valeur } }),
      });
    } catch (e) {
      // Refusé par le serveur : la case revient à sa valeur, sinon la feuille imprimée
      // montrerait « payé » alors que rien n'est enregistré.
      setLignes((ls) => ls.map((x) => (x.id === ligne.id ? { ...x, [colonne]: avant } : x)));
      setErreur(e.message);
    }
  };

  const entetes = [
    "N°",
    ...colonnes.map((c) => c.label),
    ...modifiables.map((m) => ({ label: m.label, cls: m.impression === "aucune" ? "sans-impression" : "" })),
    // Une colonne étroite par jour (« lun » au-dessus de « 05.10 ») : la feuille tient sur la largeur.
    // Les billets de juin : « Entré·e », la case à cocher à l'entrée (`fiche.case`).
    ...(joursStage.length ? joursStage.map((j) => ({
      label: <>{j.libelle.split(" ")[0]}<br />{j.libelle.split(" ").slice(1).join(" ")}</>,
      cls: "!px-1 !text-center",
    })) : [fiche.case || "Présent·e"]),
  ];

  // Même enregistrement que l'appel du mode école (un enfant à la fois).
  const marquer = async (appelId, le, ligne, valeur) => {
    setErreur("");
    const poser = (v) => (joursStage.length
      ? setLignes((ls) => ls.map((x) => (x.id === ligne.id ? { ...x, presences: { ...(x.presences || {}), [le]: v } } : x)))
      : setAppelWs((a) => ({ ...a, [ligne.id]: v })));
    const avant = joursStage.length ? (ligne.presences || {})[le] || "" : appelWs[ligne.id] || "";
    poser(valeur);
    try {
      await api(`/mobile/appel/${encodeURIComponent(appelId)}/${le}`, {
        method: "PUT", body: JSON.stringify({ etats: { [ligne.id]: valeur } }),
      });
    } catch (e) {
      poser(avant);
      setErreur(`Présence pas enregistrée : ${e.message}`);
    }
  };
  const casesPresence = (l, inscrit) => {
    if (joursStage.length) {
      const choisis = l.jours_choisis || [];
      return joursStage.map(({ date: le }) => (
        <td key={le} className="px-1 py-2 text-center">
          <CasePresence etat={(l.presences || {})[le] || ""} attendu={!choisis.length || choisis.includes(le)}
            testid="fiche-presence-jour"
            onChange={inscrit && le <= auj ? (v) => marquer(fiche.jours_stage.id, le, l, v) : null} />
        </td>
      ));
    }
    if (fiche.appel) {
      return (
        <td className="px-3 py-2">
          <CasePresence etat={appelWs[l.id] || ""} testid="fiche-presence"
            onChange={inscrit && fiche.appel.le <= auj ? (v) => marquer(fiche.appel.id, fiche.appel.le, l, v) : null} />
        </td>
      );
    }
    return <td className="px-3 py-2"><CaseVide /></td>;
  };

  // Une page par groupe : on lance l'impression une seule fois et chaque groupe sort sur
  // sa propre feuille. Une audition se découpe aussi par catégorie (la compétition a ses
  // groupes d'âge), donc l'éclatement suit TOUTES les colonnes de filtre.
  const combinaison = (l) => colonnesFiltre.map(({ colonne }) => l[colonne] || "—").join(" · ");
  const groupes = eclate && principal
    ? [...new Set(visibles.map(combinaison))].sort()
      .map((v) => ({ valeur: v, lignes: visibles.filter((l) => combinaison(l) === v) }))
    : [{ valeur: "", lignes: visibles }];
  const etiquetteGroupe = colonnesFiltre.map(({ label }) => label).join(" · ");

  // Les billets de juin (Liste › Spectacles) : leurs totaux en tête (billets, personnes, plein,
  // 3-12, 0-2, déjà entrés), et un billet peut compter plusieurs personnes.
  const billets = fiche.type === "billets";
  const Page = ({ groupe, derniere }) => {
    const imprimes = groupe.lignes.filter(estInscrit);
    const nbPersonnes = billets ? imprimes.reduce((t, l) => t + (Number(l.total) || 0), 0) : imprimes.length;
    return (
      <section data-testid="feuille-autre"
        style={{ breakAfter: derniere ? "auto" : "page" }}>
        <Entete
          eyebrow={fiche.entete.eyebrow || "Liste des participants"}
          titre={fiche.entete.titre}
          sous_titre={<>1000 ARTS · {libelleSaison(fiche, saisonCourante)}<br />{fiche.entete.sous_titre}</>}
          infos={fiche.entete.infos || [["Participants", `${imprimes.length} inscrit(s)${groupe.lignes.length !== imprimes.length ? ` · ${groupe.lignes.length - imprimes.length} annulé(s)` : ""}`],
            ...(groupe.valeur ? [[etiquetteGroupe, groupe.valeur]] : actifs.map(({ colonne, label }) => [label, filtres[colonne]]))]}
        />
        <TitreBloc nb={billets ? `${imprimes.length} billet(s) · ${nbPersonnes} personne(s)` : `${nbPersonnes} personne(s)`}>
          <span className="flex items-center gap-2">
            <Users size={14} /> {groupe.valeur ? `${etiquetteGroupe} : ${groupe.valeur}` : (fiche.entete.bloc || "Participants")}
          </span>
        </TitreBloc>
        <TableauFeuille
          testid="fiche-participants"
          entetes={entetes}
          vide="Personne dans cette sélection."
          lignes={groupe.lignes.map((l, k) => {
            const inscrit = estInscrit(l);
            return (
              <tr key={l.id || k} className={inscrit ? "" : "sans-impression"}
                style={{ background: inscrit ? (k % 2 ? "#F9FBFC" : "#fff") : "#FEF2F2", borderTop: "1px solid #E9EEF3" }}>
                <td className="px-3 py-2 text-[var(--muted)]">{inscrit ? imprimes.indexOf(l) + 1 : "—"}</td>
                {colonnes.map(({ colonne }) => (
                  <td key={colonne} className={`px-3 py-2 ${colonne === "nom" || colonne === "prenom" ? "font-semibold" : "text-[var(--muted)]"}`}>
                    {l[colonne] === 0 ? 0 : l[colonne] || "—"}
                  </td>
                ))}
                {modifiables.map((m) => (
                  <td key={m.colonne} className={`px-3 py-2 ${m.impression === "aucune" ? "sans-impression" : ""}`}>
                    <SelectLigne valeur={l[m.colonne]} options={m.options} testid={`fiche-${m.colonne}`}
                      onChange={(v) => modifier(l, m.colonne, v)} />
                    {m.impression === "valeur" && <span className="impression-inline">{l[m.colonne] || "—"}</span>}
                    {m.impression === "vide" && (
                      <span className="impression-inline" style={{ minWidth: 90, borderBottom: "1px solid #94A3B8" }}>&nbsp;</span>
                    )}
                  </td>
                ))}
                {casesPresence(l, inscrit)}
              </tr>
            );
          })}
        />
        <div className="mt-4 text-[10px] text-[var(--muted)] pied-feuille">
          1000 ARTS · Chemin des Dailles 10E, 1053 Cugy VD · info1000arts@gmail.com
        </div>
      </section>
    );
  };

  return (
    <div>
      <div className="sans-impression mb-4 flex flex-wrap items-center gap-3">
        <Recherche valeur={q} onChange={setQ} largeur={300} testid="fiche-recherche"
          placeholder={billets ? "Nom, prénom ou n° de billet" : "Chercher : nom, e-mail, téléphone…"} />
        {cherche && (
          <span className="text-xs font-semibold" style={{ color: "var(--primary)" }}
            data-testid="fiche-recherche-active">
            {visibles.length} {billets ? "billet(s)" : "personne(s)"} sur {lignes.length}
          </span>
        )}
        {billets && !cherche && (
          <span className="text-xs text-[var(--muted)]" data-testid="fiche-billets-aide">
            Les billets payés, par nom de famille. Si un QR code ne passe pas à l'entrée, on cherche le nom ici ; sur le papier, on coche « Entré·e ».
          </span>
        )}
      </div>
      {(colonnesFiltre.length > 0 || modifiables.length > 0) && (
        <div className="space-y-3 sans-impression mb-4" data-testid="fiche-filtres">
          <p className="text-xs text-[var(--muted)]">
            {colonnesFiltre.length > 0 && "Choisis le groupe à imprimer : seules les personnes affichées partiront à l'impression. "}
            {modifiables.length > 0 && "Les menus déroulants ne s'impriment pas, et une personne annulée disparaît du papier."}
          </p>
          {principal && (
            <button className="btn-ghost" data-testid="fiche-tous-groupes" onClick={() => setEclate(!eclate)}
              style={eclate ? { borderColor: "var(--primary)", color: "var(--primary)" } : undefined}>
              <Printer size={13} /> {eclate
                ? "Revenir à une seule liste"
                : `Imprimer tous les groupes — une page par ${etiquetteGroupe.toLowerCase()}`}
            </button>
          )}
          {!eclate && colonnesFiltre.map(({ colonne, label }) => (
            <div key={colonne} className="flex flex-wrap items-center gap-3">
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]" style={{ minWidth: 96 }}>{label}</span>
              <Puces testid={`fiche-filtre-${colonne}`} valeur={filtres[colonne] || ""} options={options(colonne)}
                onChange={(v) => setFiltres({ ...filtres, [colonne]: v })} />
            </div>
          ))}
          {eclate && (
            <p className="text-sm font-semibold" style={{ color: "var(--primary)" }} data-testid="fiche-eclate">
              {groupes.length} page(s) prête(s) : {groupes.map((g) => g.valeur).join(" · ")}
            </p>
          )}
        </div>
      )}

      {!eclate && actifs.length > 0 && (
        <p className="text-sm mb-3 font-semibold sans-impression" style={{ color: "var(--primary)" }} data-testid="fiche-selection">
          {actifs.map(({ colonne, label }) => `${label} : ${filtres[colonne]}`).join(" · ")}
        </p>
      )}
      {(joursStage.length > 0 || fiche.appel) && (
        <p className="text-xs text-[var(--muted)] mb-3 sans-impression" data-testid="fiche-presences-aide">
          Présences : l'appel fait au téléphone (mode école), ✓ présent·e, ✗ absent·e ; un clic corrige un jour passé.
          {joursStage.length > 0 && " « — » : l'enfant ne vient pas ce jour-là (jours cochés dans Facturation › Stages › « Modifier » ou au téléphone)."}
        </p>
      )}
      {erreur && <p className="text-sm mb-3 sans-impression" style={{ color: "#B91C1C" }} data-testid="fiche-erreur">{erreur}</p>}

      {groupes.map((g, i) => <Page key={g.valeur || "tous"} groupe={g} derniere={i === groupes.length - 1} />)}
    </div>
  );
}

/* Conservé pour la fiche élève, qui affiche ses cours d'essai dans le même style. */
export const Bloc = ({ titre, lignes, colonnes, entetes, testid }) => (
  <Section titre={<span>{titre} <span className="text-sm text-[var(--muted)]">({lignes.length})</span></span>} testid={`${testid}-section`}>
    <Tableau
      testid={testid}
      entetes={entetes}
      lignes={lignes}
      rendu={(l) => (
        <>
          {colonnes.map((c) => (
            <Cellule key={c} muet={c !== "eleve" && c !== "prenom" && c !== "nom"} fort={c === "eleve"}>
              {l[c] === 0 ? 0 : l[c] || "—"}
            </Cellule>
          ))}
        </>
      )}
    />
  </Section>
);
